import {
  Component,
  Injector,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  computed,
  effect,
  inject,
  viewChild,
  type EffectRef,
  type Signal,
} from '@angular/core';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, takeUntil } from 'rxjs/operators';
import {
  TaskPluginConfig,
  CurrentUserInfo,
  TaskItem,
  SubtaskLinkCopiedEvent,
  TaskEntityLink,
  TaskInterfacePanelComponent,
  TaskContextSyncService,
  type TaskCreatePayload,
} from '@mtm/task-ui-angular';
import { MtmContextService } from '@mtm/context-ui-angular';

import { ClickableNotificationService } from '../../services/clickable-notification.service';
import { AuthService } from '../../services/auth.service';
import { PlatformTaskDataService } from '../../services/platform-task-data.service';
import { PlatformRbacSignatureService, SignatureRequestResult } from '../../services/platform-rbac-signature.service';
import { RightPanelCoordinatorService } from '../../services/right-panel-coordinator.service';
import { UserService } from '../../services/user.service';
import { Params, Router } from '@angular/router';
import {
  PlatformTaskInterfaceContext,
  PlatformTaskInterfaceStateService,
} from '../../services/platform-task-interface-state.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../platform-comments-chat/platform-chat-entity.const';
import { WorkspaceService } from '../../services/workspace.service';
import { TimelineService } from '../../services/timeline.service';
import { DriveCampaign, WorkspaceDTO } from '../../interfaces';
import { CryptoService } from '../../services/crypto.service';
import { ProjectService } from '../../services/project.service';
import { GeneralUtilsService } from '../../services/general-utils.service';
import { OverlayService } from '../../services/overlayService';
import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  railCollapseItem,
  TOGGLE_RAIL_COLLAPSE,
  type ToggleRailItem,
  type ToggleRailSelection,
} from '@mtm/media-studio';
import { DriveRightRailService } from '../../drive/drive-right-rail.service';
import { VIEW_CONTEXT_KEY } from '../../services/view-context-keys';
import { followContextTask } from './follow-context-task';

@Component({
  selector: 'mtm-platform-task-interface',
  templateUrl: './platform-task-interface.component.html',
  styleUrl: './platform-task-interface.component.scss',
  standalone: false,
})
export class PlatformTaskInterfaceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() entityType: string;
  @Input() pageId: string;
  @Input() entityName: string;
  @Input() projectId?: string | null;
  @Output() isLoadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  taskConfig: TaskPluginConfig | undefined;
  pluginReady: boolean = false;

  private readonly authService: AuthService = inject(AuthService);
  private readonly taskDataService: PlatformTaskDataService = inject(PlatformTaskDataService);
  private readonly panelCoordinator: RightPanelCoordinatorService = inject(RightPanelCoordinatorService);
  private readonly userService: UserService = inject(UserService);
  private readonly router: Router = inject(Router);
  private readonly taskStateService: PlatformTaskInterfaceStateService = inject(PlatformTaskInterfaceStateService);
  private readonly workspaceService: WorkspaceService = inject(WorkspaceService);
  private readonly timelineService: TimelineService = inject(TimelineService);
  private readonly cryptoService: CryptoService = inject(CryptoService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly generalUtilsService: GeneralUtilsService = inject(GeneralUtilsService);
  private readonly overlayService: OverlayService = inject(OverlayService);
  private readonly translatePipe: TranslatePipe = inject(TranslatePipe);
  /** Drive surfaces: the right rail's buttons move into this panel's header while it is open (PS2). */
  protected readonly driveRightRail: DriveRightRailService = inject(DriveRightRailService);
  protected readonly headerRailItems: Signal<readonly ToggleRailItem[]> = computed(() => [
    ...this.driveRightRail.items(),
    railCollapseItem(this.translatePipe.transform('collapse')),
  ]);
  private readonly rbacSignature: PlatformRbacSignatureService = inject(PlatformRbacSignatureService);
  private readonly viewContext: MtmContextService = inject(MtmContextService);
  private readonly taskContextSync: TaskContextSyncService = inject(TaskContextSyncService);
  private readonly injector: Injector = inject(Injector);
  private readonly clickableNotification: ClickableNotificationService = inject(ClickableNotificationService);

  private readonly panel: Signal<TaskInterfacePanelComponent | undefined> = viewChild(TaskInterfacePanelComponent);

  /**
   * Whether the task panel is on, as the top nav last recorded it in `ts`.
   *
   * Read back off the view context rather than re-derived: `ts` is true only
   * when the right panel is expanded *and* showing tasks, and no single service
   * says that — `RightPanelCoordinatorService` knows which panel is selected but
   * not whether it is expanded. Taking the top nav's own answer keeps the two
   * from drifting apart.
   *
   * Tracks the context state directly instead of re-reading on `NavigationEnd`:
   * a flag now changes the moment it is written, while the navigation that
   * swaps `ctx` waits on a round trip that may not happen at all if the store
   * is unreachable.
   */
  private readonly isTaskPanelOn: Signal<boolean> = computed(
    () => this.viewContext.state()[VIEW_CONTEXT_KEY.task] === true
  );

  constructor() {
    // The sidebar's own context keys (tc/tid/tt) are restored and mirrored by
    // the task library itself; the wrapper only declares when the panel is
    // visible at all, so closing it clears them — including keys stranded by
    // an earlier session on a page where the panel never initializes.
    this.taskContextSync.watchGate(this.isTaskPanelOn, this.injector);
    followContextTask(this.panel, this.isTaskPanelOn, () => this.pluginReady, this.injector);
  }

  /**
   * Opens the panel's create-task form when one was requested (e.g. drive
   * calendar click). Tracks the panel viewChild and the pending flag, so a
   * request made while the panel is still initializing fires once the `@if`
   * renders it. The plain-field `pluginReady` guard skips a stale panel
   * instance mid-reinit; the subsequent false→true flip re-renders the `@if`,
   * re-emitting the viewChild and re-firing this effect.
   */
  private readonly openCreateTaskOnRequest: EffectRef = effect(() => {
    const panel: TaskInterfacePanelComponent | undefined = this.panel();
    if (!panel || !this.taskStateService.pendingCreateTask() || !this.pluginReady) {
      return;
    }
    const defaults = this.taskStateService.pendingCreateTaskDefaults();
    this.taskStateService.clearCreateTaskRequest();
    panel.onNewTaskClick(defaults ?? undefined);
  });

  /**
   * Opens a specific task's detail view when one was requested (e.g. drive
   * calendar click on a directory that already has a default task). Same
   * consume-once pattern as `openCreateTaskOnRequest`. A definite 404 is
   * reported back so the requester can clear its stale reference; any other
   * failure just leaves the task list visible.
   */
  private readonly openTaskOnRequest: EffectRef = effect(() => {
    const panel: TaskInterfacePanelComponent | undefined = this.panel();
    const taskUuid: string | null = this.taskStateService.pendingOpenTask();
    if (!panel || !taskUuid || !this.pluginReady) {
      return;
    }
    this.taskStateService.clearOpenTaskRequest();
    panel
      .openTaskByUuid(taskUuid)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: 'opened' | 'missing' | 'error') => {
        if (result === 'missing') {
          this.taskStateService.notifyOpenTaskMissing(taskUuid);
        }
      });
  });

  private initSub: Subscription | null = null;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private readonly reinitialize$ = new Subject<void>();

  private activeCampaign: DriveCampaign | null = null;
  private hasPerformedFullInit: boolean = false;

  ngOnInit(): void {
    this.reinitialize$.pipe(debounceTime(0), takeUntil(this.ngUnsubscribe)).subscribe(() => this.fetchAndInit());

    this.panelCoordinator.activePanel$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((panel) => panel === 'task')
      )
      .subscribe(() => this.handlePanelActivation());

    if (this.panelCoordinator.isActive('task')) {
      this.handlePanelActivation();
    }

    this.timelineService.activeCampaign$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((campaign: DriveCampaign) => {
      this.activeCampaign = campaign;
    });
  }

  private handlePanelActivation(): void {
    if (!this.pageId || !this.entityType) return;

    if (!this.hasPerformedFullInit) {
      this.scheduleReinitialize();
      return;
    }

    if (this.panelCoordinator.needsReinitialization('task', this.pageId, this.entityType)) {
      this.scheduleReinitialize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.panelCoordinator.isActive('task')) return;

    if (!this.hasPerformedFullInit && this.pageId && this.entityType) {
      this.scheduleReinitialize();
      return;
    }

    const pageIdChanged: boolean =
      !!changes['pageId'] &&
      !changes['pageId'].firstChange &&
      changes['pageId'].currentValue !== changes['pageId'].previousValue;
    const entityTypeChanged: boolean =
      !!changes['entityType'] &&
      !changes['entityType'].firstChange &&
      changes['entityType'].currentValue !== changes['entityType'].previousValue;
    const entityNameChanged: boolean =
      !!changes['entityName'] &&
      !changes['entityName'].firstChange &&
      changes['entityName'].currentValue !== changes['entityName'].previousValue;

    if (pageIdChanged || entityTypeChanged) {
      this.scheduleReinitialize();
      return;
    }

    if (entityNameChanged) {
      this.applyEntityNameUpdate();
    }
  }

  private applyEntityNameUpdate(): void {
    const entity = this.taskConfig?.entity;
    if (!entity) return;
    const nextName: string | undefined = this.entityName || entity.entityName;
    if (nextName === entity.entityName) return;
    this.taskConfig = {
      ...this.taskConfig,
      entity: { ...entity, entityName: nextName },
    };
  }

  private scheduleReinitialize(): void {
    this.pluginReady = false;
    this.taskConfig = undefined;
    this.reinitialize$.next();
  }

  private fetchAndInit(): void {
    if (!this.entityType || !this.pageId) {
      this.isLoadingChange.emit(false);
      return;
    }

    this.isLoadingChange.emit(true);
    this.initSub?.unsubscribe();

    const participants$ = this.taskDataService.getParticipants(this.entityType, this.pageId);
    const signature$ = this.rbacSignature
      .requestSignature(this.entityType, this.pageId, this.entityName, this.projectId)
      .pipe(catchError(() => of<SignatureRequestResult | null>(null)));

    this.initSub = forkJoin({ participants: participants$, signature: signature$ })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({ participants, signature }) => {
          const authUser = this.authService.getAuthUser();
          const currentUser: CurrentUserInfo = {
            username: authUser?.username ?? '',
            firstName: authUser?.firstName,
            lastName: authUser?.lastName,
            avatarUrl: authUser?.username ? this.userService.getAvatarUrl(authUser.username) : undefined,
          };

          this.taskConfig = {
            airbacToken: signature?.signature?.access_token ?? '',
            currentUser,
            participants,
            entity: {
              entityUuid: this.pageId,
              entityType: this.entityType,
              entityName: this.entityName || signature?.itemName,
            },
            rbacToken: signature?.signature?.access_token,
            entityLoader: (parentType: string, parentUuid: string) =>
              this.taskDataService.getEntities(parentType, parentUuid, 1),
            entityRbacTokenProvider: (eType: string, eUuid: string, eName?: string) =>
              this.taskDataService.generateEntityRbacToken(eType, eUuid, eName),
            entityParticipantsProvider: (eType: string, eUuid: string) =>
              this.taskDataService.getParticipants(eType, eUuid),
            onEntityClick: (link: TaskEntityLink, options?: { newTab?: boolean }): void =>
              void this.clickableNotification.openTaskEntity(link, options?.newTab),
            boardUuid: '',
          } as TaskPluginConfig;
          this.pluginReady = true;
          this.hasPerformedFullInit = true;
          this.panelCoordinator.markInitialized('task', this.pageId, this.entityType);
          this.isLoadingChange.emit(false);
        },
        error: () => {
          // A pending create-task/open-task request must not survive a failed
          // init — it would fire on an unrelated later activation.
          this.taskStateService.clearCreateTaskRequest();
          this.taskStateService.clearOpenTaskRequest();
          this.isLoadingChange.emit(false);
          this.initSub = null;
        },
        complete: () => {
          this.initSub = null;
        },
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.isLoadingChange.emit(false);
    this.initSub?.unsubscribe();
  }

  public onViewAll(): void {
    const [routePath, queryParams] = this.getTaskRoutePath();
    this.router.navigate(routePath, { queryParams });
  }

  public onNewTask(): void {
    const [routePath, queryParams] = this.getTaskRoutePath();
    routePath.push('new');
    this.router.navigate(routePath, { queryParams });
  }

  private getTaskRoutePath(): [string[], Params] {
    const { activeCampaign } = this;
    const queryParams: Params = {};

    const context: PlatformTaskInterfaceContext = this.taskStateService.getCurrentValue();
    const activeWorkspace: WorkspaceDTO | undefined = this.workspaceService.getActiveWorkspace();
    const workspaceId: string = activeWorkspace ? this.cryptoService.encryptString(activeWorkspace.id) : '';
    const campaignId: string = activeCampaign ? this.cryptoService.encryptString(activeCampaign.id) : '';
    const projectId: string = this.projectService.project?.id ?? '';

    const routePath: string[] = [];
    switch (context.entityType) {
      case PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE:
        routePath.push(`/workspaces/${workspaceId}/tasks`);
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.CAMPAIGN:
        routePath.push(`/workspaces/${workspaceId}/campaign-drive/${campaignId}/tasks`);
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.PROJECT:
        if (context.pageId) {
          routePath.push(`/projects/${context.pageId}/tasks`);
        } else {
          routePath.push('/tasks');
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.SECTION:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.sectionId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.SUB_SECTION:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.subsectionId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.FOLDER:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.folderId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.SUB_FOLDER:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.subFolderId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.VERSION:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.versionId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      case PLATFORM_CHAT_ENTITY_TYPE.FILE:
        if (projectId) {
          routePath.push(`/projects/${projectId}/tasks`);
          queryParams.fileId = context.pageId;
        } else {
          routePath.push('/tasks');
          if (context.entityType && context.pageId) {
            queryParams.entityType = context.entityType;
            queryParams.entityUuid = context.pageId;
            if (context.entityName) queryParams.entityName = context.entityName;
          }
        }
        break;
      default:
        routePath.push('/tasks');
        break;
    }
    return [routePath, queryParams];
  }

  /** Relays a created task's uuid to whoever requested the create (e.g. drive calendar flow). */
  public onPanelTaskCreated(payload: TaskCreatePayload): void {
    if (!payload.taskUuid) {
      return;
    }
    this.taskStateService.notifyTaskCreated({ taskUuid: payload.taskUuid, entityId: payload.entityId ?? null });
  }

  public onLinkCopied(taskId: string): void {
    if (!taskId) {
      return;
    }

    const [routePath, queryParams] = this.getTaskRoutePath();
    routePath.push(taskId);
    const taskUrl: string =
      globalThis.location.origin + this.router.createUrlTree(routePath, { queryParams }).toString();
    this.generalUtilsService.copyToClipboard(taskUrl);
    this.overlayService.showSuccess(this.translatePipe.transform('copiedToClipboard'));
  }

  public onSubtaskLinkCopied(event: SubtaskLinkCopiedEvent): void {
    if (!event.subtaskId || !event.parentTaskId) {
      return;
    }

    const [routePath, queryParams] = this.getTaskRoutePath();
    routePath.push(event.parentTaskId);
    queryParams.subtaskId = event.subtaskId;
    const subtaskUrl: string =
      globalThis.location.origin + this.router.createUrlTree(routePath, { queryParams }).toString();
    this.generalUtilsService.copyToClipboard(subtaskUrl);
    this.overlayService.showSuccess(this.translatePipe.transform('copiedToClipboard'));
  }

  public onEditTask(task: TaskItem): void {
    const taskId: string | undefined = task.id;

    if (!taskId) {
      return;
    }

    const [routePath, queryParams] = this.getTaskRoutePath();
    routePath.push(taskId);
    this.router.navigate(routePath, { queryParams });
  }

  protected onHeaderRailItem({ id, trigger }: ToggleRailSelection): void {
    if (id === TOGGLE_RAIL_COLLAPSE) {
      this.driveRightRail.setTasksOpen(false);
    } else {
      this.driveRightRail.select(id, trigger);
    }
  }
}
